import { useState, useEffect } from "react"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Plus, X } from "lucide-react"
import type { FrontMatterField } from "./SettingsPage"

function getCSRFToken(): string {
  const meta = document.querySelector('meta[name="csrf-token"]')
  return meta?.getAttribute("content") || ""
}

interface FieldModalProps {
  open: boolean
  onClose: () => void
  onSave: (field: FrontMatterField) => void
  field: FrontMatterField | null
}

export function FieldModal({ open, onClose, onSave, field }: FieldModalProps) {
  const [name, setName] = useState("")
  const [options, setOptions] = useState<string[]>([])
  const [newOption, setNewOption] = useState("")
  const [saving, setSaving] = useState(false)
  const [errors, setErrors] = useState<Record<string, string[]>>({})

  useEffect(() => {
    if (open) {
      if (field) {
        setName(field.name)
        setOptions(field.options.map((o) => o.label))
      } else {
        setName("")
        setOptions([])
      }
      setNewOption("")
      setErrors({})
    }
  }, [open, field])

  function handleAddOption() {
    const trimmed = newOption.trim()
    if (!trimmed || options.includes(trimmed)) return
    setOptions([...options, trimmed])
    setNewOption("")
  }

  function handleRemoveOption(index: number) {
    setOptions(options.filter((_, i) => i !== index))
  }

  async function handleSave() {
    setSaving(true)
    setErrors({})

    const url = field ? `/api/front_matter_fields/${field.id}` : "/api/front_matter_fields"
    const method = field ? "PATCH" : "POST"

    try {
      const res = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": getCSRFToken(),
        },
        body: JSON.stringify({
          name,
          options: options.map((label) => ({ label })),
        }),
      })

      if (res.ok) {
        const saved = await res.json()
        onSave(saved)
      } else {
        const data = await res.json()
        setErrors(data.errors || {})
      }
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{field ? "Edit Field" : "Add Field"}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div>
            <Label>Field Name</Label>
            <Input
              className="mt-1"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Category, Tags, Status"
            />
            {errors.name && (
              <p className="mt-1 text-sm text-destructive">{errors.name.join(", ")}</p>
            )}
          </div>

          <div>
            <Label>Options</Label>
            <div className="mt-2 space-y-2">
              {options.map((opt, i) => (
                <div key={i} className="flex items-center gap-2">
                  <span className="flex-1 rounded border px-3 py-1.5 text-sm">{opt}</span>
                  <button
                    onClick={() => handleRemoveOption(i)}
                    className="text-muted-foreground hover:text-destructive"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))}
              <div className="flex items-center gap-2">
                <Input
                  value={newOption}
                  onChange={(e) => setNewOption(e.target.value)}
                  placeholder="Add option..."
                  onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), handleAddOption())}
                />
                <Button variant="outline" size="sm" onClick={handleAddOption} type="button">
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving || !name.trim()}>
            {saving ? "Saving..." : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
